import { CalendarBlankIcon, MegaphoneIcon, TargetIcon } from '@phosphor-icons/react';
import React from 'react';

import { InspectorHeader } from '@/components/ui/inspector/InspectorHeader';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';
import { ITEM_TITLE } from '@/lib/ui-tokens';
import { useStrategyDialogStore } from '@/stores/useStrategyDialogStore';

interface StrategyBodyProps {
  projectId: string;
  onClose: () => void;
  breadcrumbs?: InspectorBreadcrumbItem[];
}

/**
 * Read-only strategy viewer mounted by the Inspector when a strategy artifact
 * is opened from /library. Reads the payload from useStrategyDialogStore.
 *
 * NOT the same as PlanStrategyCanvas in /create — that one is editable, hooks
 * into chat regenerate flows, and writes back to projects.strategy. This one
 * is a viewer; deep edits happen in /create or /projects/:id.
 */
export const StrategyBody: React.FC<StrategyBodyProps> = ({ onClose, breadcrumbs }) => {
  const payload = useStrategyDialogStore((s) => s.payload);
  if (!payload) return null;

  const { strategy, projectName } = payload;
  const pillars = strategy.pillars ?? [];
  const schedule = strategy.schedule ?? [];
  const hasStructured =
    !!strategy.goal ||
    !!(strategy as { overview?: string }).overview ||
    pillars.length > 0 ||
    schedule.length > 0;

  return (
    <div className="flex h-full flex-col">
      <InspectorHeader
        title={projectName}
        breadcrumbs={breadcrumbs}
        onClose={onClose}
        className="border-b border-border/40"
      />

      <div className="flex-1 overflow-y-auto px-5 py-6">
        <div className="mx-auto w-full max-w-2xl space-y-6">
          {strategy.goal && (
            <section>
              <div className="flex items-center gap-2 text-detail uppercase tracking-wide text-muted-foreground">
                <TargetIcon className="size-3.5" weight="regular" />
                <span>Goal</span>
              </div>
              <p className="mt-1.5 text-sm text-foreground/90 leading-relaxed">
                {String(strategy.goal)
                  .replace(/_/g, ' ')
                  .replace(/^\w/, (c) => c.toUpperCase())}
              </p>
            </section>
          )}
          {(strategy as { overview?: string }).overview && (
            <section>
              <div className="flex items-center gap-2 text-detail uppercase tracking-wide text-muted-foreground">
                <span>Overview</span>
              </div>
              <p className="mt-1.5 text-sm text-foreground/85 leading-relaxed">
                {(strategy as { overview?: string }).overview}
              </p>
            </section>
          )}

          {pillars.length > 0 && (
            <section>
              <div className="flex items-center gap-2 text-detail uppercase tracking-wide text-muted-foreground">
                <MegaphoneIcon className="size-3.5" weight="regular" />
                <span>Pillars</span>
              </div>
              <ul className="mt-1.5 space-y-2">
                {pillars.map((p, i) => (
                  <li key={p.id ?? i} className="rounded-md border border-border/50 bg-card/40 p-3">
                    <div className={ITEM_TITLE}>{p.name}</div>
                    {p.description && (
                      <div className="mt-1 text-detail text-muted-foreground leading-relaxed">
                        {p.description}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {schedule.length > 0 && (
            <section>
              <div className="flex items-center gap-2 text-detail uppercase tracking-wide text-muted-foreground">
                <CalendarBlankIcon className="size-3.5" weight="regular" />
                <span>Schedule · {schedule.length} slots</span>
              </div>
              <ul className="mt-1.5 space-y-1">
                {schedule.slice(0, 12).map((s, i) => (
                  <li key={i} className="flex items-center gap-2 px-3 py-2">
                    <span className="text-detail font-medium text-foreground/80 w-20 shrink-0">
                      {s.dayOfWeek} · {s.time ?? '09:00'}
                    </span>
                    <span className="text-detail text-muted-foreground capitalize">
                      {s.platform}
                    </span>
                    <span className="text-muted-foreground/40">·</span>
                    <span className="text-detail text-foreground/70 truncate">{s.topic}</span>
                  </li>
                ))}
                {schedule.length > 12 && (
                  <li className="px-3 py-2 text-detail text-muted-foreground">
                    +{schedule.length - 12} more
                  </li>
                )}
              </ul>
            </section>
          )}

          {!hasStructured && (
            <div className="flex min-h-full items-center justify-center py-12">
              <div className="max-w-sm text-center">
                <p className="text-sm font-medium text-foreground">No strategy content</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Mora found the record, but there isn&apos;t a readable strategy payload yet.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
